/**
 * Resolve which tenant scope the client-portal pages should query for.
 *
 * Without impersonation:
 *   - role=client          → filter by ``currentUser.client_id``.
 *   - role=facility_manager→ filter by ``currentUser.id`` (manager_id).
 *   - consultant in FM view→ filter by the ViewMode effective FM id.
 *
 * While an admin is acting-as another user (``ImpersonationContext``
 * non-null), we substitute the impersonated user's id and client_id so
 * the portal page asks the backend for *that* user's data — the
 * ``X-Act-As`` header already scopes server-side, but the page-level
 * filter parameters also have to match or the page renders empty.
 *
 * Returns ``{clientId, managerId, scopeFilter}``. Either ``clientId``
 * or ``managerId`` is non-null when the page has a workable scope;
 * both null means "show the no-scope screen."
 */
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useViewMode } from '../contexts/ViewModeContext';

export interface PortalScope {
  clientId: string | null;
  managerId: string | null;
  /** Convenient shorthand for tasksService / facilitiesService filters.
   * Only one of the two keys is set; callers don't have to branch. */
  scopeFilter: { client_id: string } | { manager_id: string } | null;
}

export function usePortalScope(): PortalScope {
  const { currentUser } = useAuth();
  const { impersonation } = useImpersonation();
  const { effectiveFacilityManagerId } = useViewMode();

  // When impersonating, the impersonated user's role + id drives the
  // scope. Admin's own currentUser stays put for identity-aware code.
  if (impersonation) {
    if (impersonation.effectiveRole === 'client') {
      const cid = impersonation.targetClientId;
      return {
        clientId: cid,
        managerId: null,
        scopeFilter: cid ? { client_id: cid } : null,
      };
    }
    if (impersonation.effectiveRole === 'facility_manager') {
      const mid = impersonation.actAsUserId;
      return {
        clientId: null,
        managerId: mid,
        scopeFilter: { manager_id: mid },
      };
    }
    // Consultant impersonation doesn't use the client portal; fall
    // through to the consultant branch below.
  }

  const isClient = currentUser?.role === 'client';
  const isFacilityManager = currentUser?.role === 'facility_manager';
  const clientId = isClient ? (currentUser?.client_id ?? null) : null;
  const managerId = isClient
    ? null
    : isFacilityManager
      ? (currentUser?.id ?? null)
      : (effectiveFacilityManagerId ?? null);
  const scopeFilter: PortalScope['scopeFilter'] = clientId
    ? { client_id: clientId }
    : managerId
      ? { manager_id: managerId }
      : null;
  return { clientId, managerId, scopeFilter };
}
